import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { EpicPage, EpicHero, EpicCard, Chip, EPIC } from "@/components/Epic";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Clock, Users, Video, Plus, Loader2, CalendarPlus, Trash2, Radio, CheckCircle2, Globe, ArrowRight } from "lucide-react";

export const fmtWhen = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const untilText = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "now";
  const m = Math.round(ms / 60000);
  if (m < 60) return `in ${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `in ${h} hour${h === 1 ? "" : "s"}`;
  return `in ${Math.round(h / 24)} days`;
};

const downloadIcs = async (w) => {
  try {
    const res = await api.get(`/workshops/${w.id}/ics`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${w.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Couldn't build the calendar file."); }
};

function WorkshopCard({ w, onRsvp, onDelete, busy }) {
  const live = w.status === "live";
  const past = w.status === "past";
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`${EPIC} relative overflow-hidden p-5 sm:p-6 hover:shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] transition-shadow ${live ? "border-red-300 shadow-[0_0_0_4px_rgba(248,113,113,0.12)]" : ""} ${past ? "opacity-70" : ""}`} data-testid={`workshop-${w.id}`}>
      <div className={`absolute inset-y-0 left-0 w-1.5 ${live ? "bg-red-500" : past ? "bg-slate-200" : "bg-emerald-500"}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {live && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-red-500 rounded-full px-2 py-0.5 animate-pulse" data-testid="workshop-live-badge"><Radio className="w-3 h-3" /> Live now</span>}
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
              {w.audience === "everyone" ? <><Globe className="w-3 h-3" /> Everyone</> : <><Users className="w-3 h-3" /> {w.team_name || "Team"}</>}
            </span>
          </div>
          <h3 className="font-extrabold tracking-tight text-slate-900 text-xl font-display mt-1.5 leading-tight" data-testid="workshop-title">{w.title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Hosted by {w.host_name}</p>
        </div>
        <div className="shrink-0 rounded-2xl bg-slate-50 border border-slate-200/80 px-3.5 py-2.5 text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{new Date(w.starts_at).toLocaleDateString("en-GB", { weekday: "short" })}</div>
          <div className="text-2xl font-extrabold font-display tracking-tight text-slate-900 leading-none">{new Date(w.starts_at).getDate()}</div>
          <div className="text-[11px] text-slate-600">{new Date(w.starts_at).toLocaleDateString("en-GB", { month: "short" })} · {new Date(w.starts_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {w.duration_min} min · {past ? "finished" : untilText(w.starts_at)}</div>
        </div>
      </div>
      {w.description && <p className="text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-line">{w.description}</p>}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {!past && (
          <Button onClick={() => onRsvp(w)} disabled={busy === w.id} size="sm"
            className={`h-10 rounded-full px-4 font-semibold ${w.going ? "bg-emerald-100/80 text-emerald-800 border border-emerald-200 hover:bg-emerald-100" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-[0_4px_14px_rgba(16,185,129,0.35)]"}`}
            data-testid={`workshop-rsvp-${w.id}`}>
            {busy === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : w.going ? <><CheckCircle2 className="w-4 h-4 mr-1.5" /> You're going</> : "RSVP · I'll be there"}
          </Button>
        )}
        {w.meeting_link && !past && (
          <a href={w.meeting_link} target="_blank" rel="noreferrer" data-testid={`workshop-join-${w.id}`}
            className={`inline-flex items-center h-10 rounded-full px-4 text-sm font-semibold transition-colors ${live ? "bg-red-500 hover:bg-red-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:border-cyan-400 hover:text-cyan-700"}`}>
            <Video className="w-4 h-4 mr-1.5" /> {live ? "Join now" : "Meeting link"}
          </a>
        )}
        {!past && <Button variant="outline" size="sm" onClick={() => downloadIcs(w)} className="h-10 rounded-full" data-testid={`workshop-ics-${w.id}`}><CalendarPlus className="w-4 h-4 mr-1.5" /> Add to calendar</Button>}
        <span className="text-xs text-slate-500 ml-auto inline-flex items-center gap-1" data-testid={`workshop-count-${w.id}`}><Users className="w-3.5 h-3.5" /> {w.rsvp_count} going</span>
        {w.can_manage && <button onClick={() => onDelete(w)} className="text-slate-400 hover:text-red-600 transition-colors" title="Cancel workshop" data-testid={`workshop-delete-${w.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </motion.div>
  );
}

function CreateDialog({ open, onClose, onCreated, isAdmin, hasTeam }) {
  const blank = { title: "", description: "", date: "", time: "18:30", duration_min: 60, meeting_link: "", audience: isAdmin && !hasTeam ? "everyone" : "team" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(blank); }, [open]); // eslint-disable-line
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.title.trim() || !f.date || !f.time) return toast.error("Title, date and time are required.");
    setSaving(true);
    try {
      const starts_at = new Date(`${f.date}T${f.time}`).toISOString();
      const { data } = await api.post("/workshops", { title: f.title, description: f.description, starts_at, duration_min: Number(f.duration_min) || 60, meeting_link: f.meeting_link, audience: f.audience });
      toast.success("Workshop scheduled");
      onCreated(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't schedule the workshop."); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="workshop-create-dialog">
        <DialogHeader>
          <DialogTitle>Schedule a live workshop</DialogTitle>
          <DialogDescription>Your reps will see it here, can RSVP, add it to their calendar and get a reminder on their dashboard.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title — e.g. Price Objection Clinic" className="bg-slate-50 border-slate-200 h-11" data-testid="workshop-title-input" />
          <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What you'll cover and what reps should bring" className="bg-slate-50 border-slate-200 min-h-[90px]" data-testid="workshop-desc-input" />
          <div className="grid grid-cols-3 gap-2">
            <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="bg-slate-50 border-slate-200 h-11" data-testid="workshop-date-input" />
            <Input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} className="bg-slate-50 border-slate-200 h-11" data-testid="workshop-time-input" />
            <Input type="number" min={15} max={480} step={15} value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="bg-slate-50 border-slate-200 h-11" placeholder="Minutes" data-testid="workshop-duration-input" />
          </div>
          <Input value={f.meeting_link} onChange={(e) => set("meeting_link", e.target.value)} placeholder="Meeting link (Zoom / Google Meet / Teams)" className="bg-slate-50 border-slate-200 h-11" data-testid="workshop-link-input" />
          <div className="flex gap-2" data-testid="workshop-audience-picker">
            {hasTeam && <button type="button" onClick={() => set("audience", "team")} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 ${f.audience === "team" ? "bg-emerald-500 text-black border-emerald-500" : "bg-white border-slate-300 text-slate-700"}`} data-testid="workshop-audience-team"><Users className="w-4 h-4" /> My team</button>}
            {isAdmin && <button type="button" onClick={() => set("audience", "everyone")} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-1.5 ${f.audience === "everyone" ? "bg-emerald-500 text-black border-emerald-500" : "bg-white border-slate-300 text-slate-700"}`} data-testid="workshop-audience-everyone"><Globe className="w-4 h-4" /> Everyone</button>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="workshop-submit-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Workshops() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/workshops").then((r) => setData(r.data)).catch(() => setData({ items: [], can_host: false }));
  useEffect(() => { load(); }, []);

  const rsvp = async (w) => {
    setBusy(w.id);
    try {
      const { data: fresh } = await api.post(`/workshops/${w.id}/rsvp`);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.id === w.id ? fresh : x)) }));
      toast.success(fresh.going ? "You're on the list — see you there." : "RSVP removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update your RSVP."); } finally { setBusy(null); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Cancel "${w.title}"?`)) return;
    try { await api.delete(`/workshops/${w.id}`); setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== w.id) })); toast.success("Workshop cancelled"); }
    catch { toast.error("Couldn't cancel."); }
  };

  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const upcoming = data.items.filter((w) => w.status !== "past");
  const past = data.items.filter((w) => w.status === "past").reverse();

  const nextGoing = upcoming.find((w) => w.going);
  return (
    <EpicPage testId="workshops-page">
      <EpicHero overline="Live training" title="Live" accent="workshops" subtitle="Sessions with Linden and your manager. RSVP, add them to your calendar, and turn up with a real objection to work on." glow="bg-cyan-300/20"
        aside={data.can_host ? (
          <Button onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-12 rounded-xl px-5 shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:-translate-y-0.5 transition-all" data-testid="workshop-create-btn"><Plus className="w-4 h-4 mr-1.5" /> Schedule workshop</Button>
        ) : nextGoing ? (
          <div className="rounded-2xl bg-white/80 backdrop-blur border border-slate-200/80 shadow-sm p-4 sm:min-w-[260px]" data-testid="next-workshop-card">
            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">Your next session</div>
            <div className="font-bold text-slate-900 mt-1 truncate">{nextGoing.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{fmtWhen(nextGoing.starts_at)} · {untilText(nextGoing.starts_at)}</div>
          </div>
        ) : null}>
        <Chip dark><CalendarDays className="w-3.5 h-3.5" /> {upcoming.length} upcoming</Chip>
        <Chip><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> {upcoming.filter((w) => w.going).length} you're attending</Chip>
      </EpicHero>

      {upcoming.length === 0 && (
        <EpicCard className="p-10 text-center" testId="workshops-empty">
          <CalendarDays className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="text-slate-700 font-medium mt-3">No upcoming workshops yet.</p>
          <p className="text-sm text-slate-500 mt-1">{data.can_host ? "Schedule one for your team above." : "Your manager or Linden will post the next session here."}</p>
        </EpicCard>
      )}
      <div className="grid gap-4" data-testid="workshops-upcoming">
        {upcoming.map((w) => <WorkshopCard key={w.id} w={w} onRsvp={rsvp} onDelete={remove} busy={busy} />)}
      </div>

      {past.length > 0 && (
        <div>
          <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-700 mb-3">Past workshops</h2>
          <div className="grid gap-3" data-testid="workshops-past">{past.map((w) => <WorkshopCard key={w.id} w={w} onRsvp={rsvp} onDelete={remove} busy={busy} />)}</div>
        </div>
      )}

      <CreateDialog open={creating} onClose={() => setCreating(false)} onCreated={(w) => setData((d) => ({ ...d, items: [...d.items, w].sort((a, b) => a.starts_at.localeCompare(b.starts_at)) }))} isAdmin={user?.role === "admin"} hasTeam={!!user?.team_id} />
    </EpicPage>
  );
}

export function WorkshopReminder() {
  const [next, setNext] = useState(null);
  useEffect(() => {
    api.get("/workshops").then((r) => {
      const soon = (r.data.items || []).filter((w) => w.going && w.status !== "past" && new Date(w.starts_at) - Date.now() < 48 * 3600 * 1000);
      setNext(soon[0] || null);
    }).catch((err) => console.warn("[Workshops] request failed", err));
  }, []);
  if (!next) return null;
  const live = next.status === "live";
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-4 flex items-center gap-3 border ${live ? "bg-red-50 border-red-300" : "bg-emerald-500/10 border-emerald-500/30"}`} data-testid="workshop-reminder">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${live ? "bg-red-500 text-white" : "bg-emerald-500 text-black"}`}>{live ? <Radio className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{live ? "Live now" : "Workshop reminder"}</div>
        <div className="font-semibold text-slate-900 truncate">{next.title} <span className="text-slate-500 font-normal">· {live ? "happening now" : `starts ${untilText(next.starts_at)}`} · {fmtWhen(next.starts_at)}</span></div>
      </div>
      {next.meeting_link
        ? <a href={next.meeting_link} target="_blank" rel="noreferrer" className={`inline-flex items-center h-10 rounded-full px-4 text-sm font-semibold ${live ? "bg-red-500 text-white hover:bg-red-600" : "bg-emerald-500 text-black hover:bg-emerald-600"}`} data-testid="workshop-reminder-join"><Video className="w-4 h-4 mr-1.5" /> {live ? "Join now" : "Open link"}</a>
        : <a href="/app/workshops" className="inline-flex items-center text-sm font-semibold text-emerald-700" data-testid="workshop-reminder-link">Details <ArrowRight className="w-4 h-4 ml-1" /></a>}
    </motion.div>
  );
}
